/**
 * Connexion au hall (menu principal) : une WebSocket sur /ws ouverte par un message `lobby`.
 * Elle reste ouverte pendant la partie pour la présence des amis et les invitations.
 * État exposé en signaux Preact ; requêtes par promesse (réponse `res` du même numéro).
 */
import { computed, signal } from '@preact/signals';
import {
  EMPTY_SOCIAL,
  LOBBY_VERSION,
  type LobbyMe,
  type LobbyNotice,
  type LobbyRequest,
  type LobbyResults,
  type LobbyServerMessage,
  type LobbyStats,
  type ServerSummary,
  type SocialState,
} from '@snowchain/shared';

export type LobbyStatus = 'idle' | 'connecting' | 'online' | 'offline' | 'auth-error';

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> };

class LobbyClient {
  readonly status = signal<LobbyStatus>('idle');
  readonly me = signal<LobbyMe | null>(null);
  readonly social = signal<SocialState>(EMPTY_SOCIAL);
  readonly stats = signal<LobbyStats>({ playersOnline: 0, worldsLive: 0 });
  readonly rtt = signal(0);
  /** Liste des serveurs (null tant qu'elle n'a pas été chargée) et date du chargement. */
  readonly servers = signal<ServerSummary[] | null>(null);
  readonly serversAt = signal(0);
  readonly serversLoading = signal(false);
  readonly onlineFriends = computed(() => this.social.value.friends.filter((f) => f.presence.state !== 'offline'));
  /** Ce qui attend une réponse du joueur : demandes reçues et invitations. */
  readonly attention = computed(() => this.social.value.incoming.length + this.social.value.invites.length);
  /** Rappel pour les faits ponctuels (toasts). */
  onNotice: ((n: LobbyNotice) => void) | null = null;
  /** Appelé quand la session n'est plus valide. */
  onAuthError: (() => void) | null = null;

  private ws: WebSocket | null = null;
  private token = '';
  private seq = 0;
  private pending = new Map<number, Pending>();
  private retry = 0;
  private closedByUser = false;
  private waiters: (() => void)[] = [];
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pingId = 0;
  /** Un écran affiche la liste des serveurs : on la recharge quand le serveur signale un changement. */
  private serversWatchers = 0;

  connect(token: string): void {
    if (this.token === token && this.ws) return;
    this.token = token;
    this.closedByUser = false;
    this.open();
  }

  close(): void {
    this.closedByUser = true;
    this.ws?.close(1000, 'fermeture');
    this.ws = null;
    this.status.value = 'idle';
  }

  private open(): void {
    this.status.value = 'connecting';
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${location.host}/ws`);
    this.ws = ws;
    ws.onopen = () => ws.send(JSON.stringify({ t: 'lobby', v: LOBBY_VERSION, token: this.token }));
    ws.onmessage = (ev) => this.onMessage(ev.data as string);
    ws.onclose = (ev) => this.onClose(ws, ev.code);
    ws.onerror = () => {
      /* onclose suit */
    };
    if (!this.pingTimer) this.pingTimer = setInterval(() => this.ping(), 10_000);
  }

  private ping(): void {
    if (this.ws?.readyState === WebSocket.OPEN && this.status.value === 'online') this.ws.send(JSON.stringify({ t: 'ping', id: ++this.pingId, ct: performance.now() }));
  }

  private onMessage(data: string): void {
    let msg: LobbyServerMessage;
    try {
      msg = JSON.parse(data) as LobbyServerMessage;
    } catch {
      return;
    }
    switch (msg.t) {
      case 'lobby-welcome':
        this.retry = 0;
        this.me.value = msg.me;
        this.social.value = msg.social;
        this.stats.value = msg.stats;
        this.status.value = 'online';
        for (const w of this.waiters.splice(0)) w();
        this.ping();
        // Reconnexion : la liste a pu changer pendant la coupure.
        if (this.serversWatchers > 0) void this.loadServers();
        break;
      case 'res': {
        const p = this.pending.get(msg.id);
        if (!p) return;
        this.pending.delete(msg.id);
        clearTimeout(p.timer);
        if (msg.ok) p.resolve(msg.data);
        else p.reject(new Error(msg.error));
        break;
      }
      case 'social':
        this.social.value = msg.social;
        break;
      case 'notice':
        this.onNotice?.(msg.n);
        break;
      case 'servers':
        this.stats.value = msg.stats;
        if (this.serversWatchers > 0) void this.loadServers();
        break;
      case 'pong':
        this.rtt.value = Math.max(0, performance.now() - msg.ct);
        break;
      case 'error':
        if (msg.code === 'auth') this.status.value = 'auth-error';
        break;
    }
  }

  private onClose(ws: WebSocket, code: number): void {
    if (this.ws !== ws) return;
    this.ws = null;
    for (const [id, p] of this.pending) {
      clearTimeout(p.timer);
      p.reject(new Error('Connexion au serveur perdue'));
      this.pending.delete(id);
    }
    if (this.closedByUser) return;
    if (code === 4004) {
      this.status.value = 'auth-error';
      this.onAuthError?.();
      return;
    }
    this.status.value = 'offline';
    const delay = Math.min(8000, 500 * 2 ** this.retry);
    this.retry = Math.min(this.retry + 1, 6);
    setTimeout(() => {
      if (!this.closedByUser && !this.ws) this.open();
    }, delay);
  }

  private whenOnline(ms = 10_000): Promise<void> {
    if (this.status.value === 'online') return Promise.resolve();
    return new Promise((resolve, reject) => {
      const t = setTimeout(() => {
        this.waiters = this.waiters.filter((w) => w !== done);
        reject(new Error('Serveur injoignable, réessayez dans un instant'));
      }, ms);
      const done = () => {
        clearTimeout(t);
        resolve();
      };
      this.waiters.push(done);
    });
  }

  /** Envoie une requête et attend la réponse (erreur : message du serveur, en français). */
  async req<K extends LobbyRequest['op']>(req: Extract<LobbyRequest, { op: K }>): Promise<LobbyResults[K]> {
    await this.whenOnline();
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) throw new Error('Serveur injoignable, réessayez dans un instant');
    const id = ++this.seq;
    return new Promise<LobbyResults[K]>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error('Pas de réponse du serveur'));
      }, 15_000);
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject, timer });
      ws.send(JSON.stringify({ t: 'req', id, req }));
    });
  }

  /** Charge la liste des serveurs (une seule requête à la fois). */
  async loadServers(): Promise<void> {
    if (this.serversLoading.value) return;
    this.serversLoading.value = true;
    try {
      const r = await this.req({ op: 'servers' });
      this.servers.value = r.servers;
      this.serversAt.value = Date.now();
    } catch {
      /* gardée telle quelle ; l'écran affiche l'état de connexion */
    } finally {
      this.serversLoading.value = false;
    }
  }

  /** Un écran affiche la liste : chargement immédiat puis à chaque signal du serveur. */
  watchServers(): () => void {
    this.serversWatchers++;
    void this.loadServers();
    return () => {
      this.serversWatchers = Math.max(0, this.serversWatchers - 1);
    };
  }

  /** Remplace une partie dans la liste après une modification (sans attendre le signal). */
  upsertServer(s: ServerSummary): void {
    const list = this.servers.value ?? [];
    const i = list.findIndex((x) => x.id === s.id);
    this.servers.value = i >= 0 ? list.map((x) => (x.id === s.id ? s : x)) : [s, ...list];
  }

  removeServer(id: string): void {
    this.servers.value = (this.servers.value ?? []).filter((x) => x.id !== id);
  }
}

export const lobby = new LobbyClient();
